import { SupportedUserRoles } from '@insurancemenu/imenu-components-lib/dist/types';
import {
  brokerRights,
  employerRights,
  enrollmentRights,
  zywaveUserRights,
} from '@insurancemenu/imenu-components-lib/dist/utils/accessRights';

export enum GeneralRouteV2Id {
  INITIATE_GROUP = 'INITIATE_GROUP',
  INITIATE_PROPOSAL = 'INITIATE_PROPOSAL',
  GROUP_HUB = 'GROUP_HUB',
}

export enum ZywaveRouteV2Id {
  ZYWAVE_GROUP_INFO = 'ZYWAVE_GROUP_INFO',
}

export enum QuoteRouteV2Id {
  QUOTE_PROPOSAL_INFO = 'QUOTE_PROPOSAL_INFO',
  QUOTE_CHECKLIST = 'QUOTE_CHECKLIST',
  QUOTE_ADD_EMPLOYEES = 'QUOTE_ADD_EMPLOYEES',
  QUOTE_ADD_EMPLOYEES_CENSUS_UPLOAD = 'QUOTE_ADD_EMPLOYEES_CENSUS_UPLOAD',
  QUOTE_ADD_EMPLOYEES_CENSUS_REVIEW = 'QUOTE_ADD_EMPLOYEES_CENSUS_REVIEW',
  QUOTE_ADD_EMPLOYEES_FULL_FORM = 'QUOTE_ADD_EMPLOYEES_FULL_FORM',
  INITIATE_SHELF_ADD_QUOTE = 'INITIATE_SHELF_ADD_QUOTE',
  ZYWAVE_QUOTE_REDIRECT = 'ZYWAVE_QUOTE_REDIRECT',
  /**
   * Temporary browsing page used to avoid showing content under the wrong page.
   * Should be replaced in the flow with the Add Quote (INITIATE_SHELF_ADD_QUOTE) page once it is ready and then removed.
   * The BE will return INITIATE_SHELF_ADD_QUOTE instead of INITIATE_SHELF_BROWSING once Add Quote is implemented.
   */
  INITIATE_SHELF_BROWSING = 'INITIATE_SHELF_BROWSING',
}

export enum EnrollmentRouteV2Id {
  EMPLOYER_FORMS = 'EMPLOYER_FORMS',
  // These are not yet reworked
  ENROLLMENT_CHECKLIST = 'ENROLLMENT_CHECKLIST',
}

/**
 * Same name for type and const mirrors old approach with `enum RouteV2Id`: a type in annotations, an object for `RouteV2Id.GROUP_HUB`.
 * Grouped enums are spread into one object for flat, enum-like access without a single monolithic enum.
 */
export type RouteV2Id = GeneralRouteV2Id | EnrollmentRouteV2Id | QuoteRouteV2Id | ZywaveRouteV2Id;

export const RouteV2Id = {
  ...GeneralRouteV2Id,
  ...QuoteRouteV2Id,
  ...ZywaveRouteV2Id,
  ...EnrollmentRouteV2Id,
};

export interface RouteData {
  /**
   * Page URL address.
   */
  path: string;
  title: string;
  /** The text used by the breadcrumb component. */
  breadcrumb?: string;
  subTitle: string;
  tutorialLink?: string;
  children?: Node[];
  /**
   * Role check function.
   * This function should take an array of user roles and return a boolean indicating whether the user has access to the route.
   */
  roleCheck?: (userRoles: SupportedUserRoles[]) => boolean;
  exact?: boolean;
}

export const GENERAL_ROUTES: Record<GeneralRouteV2Id, RouteData> = {
  [GeneralRouteV2Id.INITIATE_GROUP]: {
    path: '/initiate-group',
    roleCheck: brokerRights,
    title: 'Initiate Group',
    subTitle: 'Search for an existing group or add a new one to start your proposal',
    breadcrumb: 'Initiate Group',
  },
  [GeneralRouteV2Id.INITIATE_PROPOSAL]: {
    path: '/initiate-proposal/:groupId',
    roleCheck: enrollmentRights,
    title: 'Initiate Proposal',
    subTitle:
      'Provide the required information to initiate the proposal and review available product lines based on your input',
    breadcrumb: 'Initiate Proposal',
  },
  [GeneralRouteV2Id.GROUP_HUB]: {
    path: '/group-hub/:groupId',
    /** Any role that may open group profile from flows that link to /group-v2/:groupId. */
    roleCheck: (userRoles: SupportedUserRoles[]) => {
      return (
        brokerRights(userRoles) ||
        enrollmentRights(userRoles) ||
        employerRights(userRoles) ||
        zywaveUserRights(userRoles)
      );
    },
    title: 'Group',
    subTitle: 'Group',
    breadcrumb: 'Group',
  },
};

export const ZYWAVE_ROUTES: Record<ZywaveRouteV2Id, RouteData> = {
  [ZywaveRouteV2Id.ZYWAVE_GROUP_INFO]: {
    path: '/zywave-group/:groupId',
    roleCheck: zywaveUserRights,
    title: 'Zywave Group Info',
    subTitle: 'Zywave Group Info',
    breadcrumb: 'Group Info',
  },
};

export const QUOTE_ROUTES: Record<QuoteRouteV2Id, RouteData> = {
  [RouteV2Id.QUOTE_CHECKLIST]: {
    path: '/quote-checklist/:groupId/:periodId/:cartId',
    roleCheck: enrollmentRights,
    title: 'Proposal Checklist',
    subTitle: 'Step by step guided wizard for group setup and enrollment',
    breadcrumb: 'Checklist',
  },
  [RouteV2Id.QUOTE_PROPOSAL_INFO]: {
    path: '/quote-proposal-info/:groupId/:periodId/:cartId',
    roleCheck: enrollmentRights,
    title: 'Proposal Info',
    subTitle: 'Review and update the required information, and explore available product lines based on your input',
    breadcrumb: 'Proposal Info',
  },
  [RouteV2Id.QUOTE_ADD_EMPLOYEES]: {
    path: '/quote-add-employees/:groupId/:periodId/:cartId',
    roleCheck: enrollmentRights,
    title: 'Add Employees',
    subTitle: 'Add employee demographics and enrollments to understand the estimated costs',
    breadcrumb: 'Add Employees',
  },
  [RouteV2Id.QUOTE_ADD_EMPLOYEES_CENSUS_UPLOAD]: {
    path: '/quote-add-employees/census-upload/:groupId/:periodId/:cartId',
    roleCheck: enrollmentRights,
    title: 'Census Import',
    subTitle: 'Use our template to load the employee Census',
    breadcrumb: 'Census Import',
  },
  [RouteV2Id.QUOTE_ADD_EMPLOYEES_CENSUS_REVIEW]: {
    path: '/quote-add-employees/census-review/:groupId/:periodId/:cartId',
    roleCheck: enrollmentRights,
    title: 'Census Review',
    subTitle: 'Review the census file for the group',
    breadcrumb: 'Census Review',
  },
  [RouteV2Id.QUOTE_ADD_EMPLOYEES_FULL_FORM]: {
    path: '/quote-add-employees/full-form/:groupId/:periodId/:cartId',
    roleCheck: enrollmentRights,
    title: 'Full Employee Form',
    subTitle: 'Add an employee with all required details and optionally include dependents and benefit elections.',
    breadcrumb: 'Full Form',
  },
  [RouteV2Id.ZYWAVE_QUOTE_REDIRECT]: {
    path: '/zywave-quote-redirect/:groupId/:periodId/:cartId',
    roleCheck: zywaveUserRights,
    title: 'Quote Redirect',
    subTitle: 'Quote Redirect',
    breadcrumb: 'Quote Redirect',
  },
  /**
   * Temporary page used until the Add Quote page is implemented.
   * Once ready, the BE will return INITIATE_SHELF_ADD_QUOTE instead of INITIATE_SHELF_BROWSING.
   */
  [RouteV2Id.INITIATE_SHELF_BROWSING]: {
    path: '/browsing',
    roleCheck: enrollmentRights,
    title: 'Browse and Build Shelf Proposal',
    subTitle: 'Select plan configurations to add to a proposal and generate customer outputs',
  },
  [RouteV2Id.INITIATE_SHELF_ADD_QUOTE]: {
    path: '/add-quote/:groupId/:periodId/:cartId',
    roleCheck: enrollmentRights,
    title: 'Add Quote',
    subTitle:
      'Build your quote by selecting plans, managing employee selections and reviewing estimated costs for proposal creation',
    breadcrumb: 'Add Quote',
  },
};

/**
 *  This map contains the new V2 routes. Please break them down flow-wise: general, rfq, template, etc.
 */
export const ENROLLMENT_ROUTES: Record<EnrollmentRouteV2Id, RouteData> = {
  // TEMPORARY PAGE, NOT FULLY MIGRATED YET
  [RouteV2Id.EMPLOYER_FORMS]: {
    path: '/forms-v2/:groupId/:periodId',
    roleCheck: enrollmentRights,
    title: 'Employer Forms',
    subTitle: 'Fill out required carrier forms',
    breadcrumb: 'Employer Forms',
  },

  // TEMPORARY PAGE, NOT FULLY MIGRATED YET
  [RouteV2Id.ENROLLMENT_CHECKLIST]: {
    path: '/checklist-v2/:periodId',
    roleCheck: enrollmentRights,
    title: 'Checklist',
    subTitle: 'Checklist',
    breadcrumb: 'Checklist',
  },
};

export const ROUTES_CONFIG_V2: Record<RouteV2Id, RouteData> = Object.freeze({
  ...GENERAL_ROUTES,
  ...ZYWAVE_ROUTES,
  ...QUOTE_ROUTES,
  ...ENROLLMENT_ROUTES,
});
